/**
 * useRecorder — case-side session recording.
 *
 * Wraps MediaRecorder around the local webcam stream; flushes each
 * 30 s chunk to /api/sessions/:id/recording/chunk and finalizes via
 * /recording/finalize on stop.
 *
 * Stop semantics — both pathways converge on `stopAndFinalize`:
 *   1. `active` flips false (trainer ended / paused the session)
 *   2. Component unmounts (case closes the tab / route change)
 *
 * `stopAndFinalize` waits for the recorder's final `ondataavailable`
 * (triggered by requestData → stop) to push the last chunk onto the
 * upload chain, THEN awaits the chain, THEN calls finalize. This is
 * what makes the recording playable after a normal end_session AND
 * after a tab close while live (best-effort: the unmount path fires
 * the requests but React has already torn down — the network still
 * goes through unless the user kills the tab outright).
 *
 * Auth token: read fresh from sessionStorage inside the upload
 * closure, NOT captured at hook setup. Otherwise the access JWT
 * (2 h TTL) goes stale mid-session and uploads start 401-ing.
 */
import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../lib/api.js";

const CHUNK_MS = 30_000;
const MIME_PREFERENCES = [
  "video/webm; codecs=vp9,opus",
  "video/webm; codecs=vp8,opus",
  "video/webm",
];

interface Options {
  stream: MediaStream | null;
  sessionId: string;
  active: boolean;          // start when true, finalize when false
}

export interface RecorderState {
  recording: boolean;
  uploadedChunks: number;
  failedChunks: number;
  lastError: string | null;
}

export function useRecorder({ stream, sessionId, active }: Options): RecorderState {
  const [state, setState] = useState<RecorderState>({
    recording: false, uploadedChunks: 0, failedChunks: 0, lastError: null,
  });
  const recRef        = useRef<MediaRecorder | null>(null);
  const seqRef        = useRef(0);
  const startedAtRef  = useRef<string | null>(null);
  const uploadingRef  = useRef<Promise<void>>(Promise.resolve());
  const formatRef     = useRef<string>("");
  const finalizedRef  = useRef(false);
  const sessionIdRef  = useRef(sessionId);
  sessionIdRef.current = sessionId;

  // Drain pending uploads then POST finalize. Guarded — calling twice
  // (e.g. active→false then unmount) is a no-op after the first call.
  async function stopAndFinalize() {
    const rec = recRef.current;
    if (!rec || finalizedRef.current) return;
    finalizedRef.current = true;
    recRef.current = null;
    setState(s => ({ ...s, recording: false }));

    // Wait for MediaRecorder.stop() to deliver the final ondataavailable
    // and onstop. requestData() forces any buffered timeslice to flush
    // before the recorder shuts down so the trailing partial chunk is
    // included.
    const stoppedSignal = new Promise<void>((resolve) => {
      const prev = rec.onstop;
      rec.onstop = (ev) => { try { (prev as any)?.call(rec, ev); } catch { /* */ } resolve(); };
    });
    try { rec.requestData(); } catch { /* */ }
    try { rec.stop(); } catch { /* stop() may throw if already inactive */ }

    try {
      await stoppedSignal;
      await uploadingRef.current;
    } catch { /* */ }

    if (seqRef.current === 0) return;        // never produced a chunk
    const startedAt = startedAtRef.current;
    const durationS = startedAt
      ? (Date.now() - new Date(startedAt).getTime()) / 1000
      : null;
    try {
      const tok = sessionStorage.getItem("cognihome_access_token");
      const resp = await fetch(
        `${API_BASE}/api/sessions/${sessionIdRef.current}/recording/finalize`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${tok ?? ""}`,
          },
          body: JSON.stringify({
            count: seqRef.current,
            duration_s: durationS,
            format: formatRef.current || "video/webm",
            started_at: startedAt,
          }),
        },
      );
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j.error || `http ${resp.status}`);
      }
    } catch (e) {
      setState(s => ({ ...s, lastError: `finalize: ${(e as Error).message}` }));
    }
  }

  useEffect(() => {
    if (!stream) return;

    if (active && !recRef.current) {
      const mime = MIME_PREFERENCES.find(m => MediaRecorder.isTypeSupported(m)) ?? "";
      formatRef.current = mime;
      // Explicit bitrate caps so chunks stay ≤ ~6 MB at the 30 s
      // timeslice. Defaults can spike above 4 Mbps on high-motion
      // webcam input which pushes single chunks past the 8 MB Worker
      // cap. 1.5 Mbps video + 64 kbps audio = predictable + still
      // adequate for 1280×720 review playback.
      const opts: MediaRecorderOptions = {
        videoBitsPerSecond: 1_500_000,
        audioBitsPerSecond: 64_000,
      };
      if (mime) opts.mimeType = mime;
      let rec: MediaRecorder;
      try {
        rec = new MediaRecorder(stream, opts);
      } catch (e) {
        const err = e as Error;
        setState(s => ({ ...s, lastError: `recorder init: ${err.message}` }));
        return;
      }
      recRef.current = rec;
      seqRef.current = 0;
      finalizedRef.current = false;
      startedAtRef.current = new Date().toISOString();

      rec.ondataavailable = async (ev: BlobEvent) => {
        if (!ev.data || ev.data.size === 0) return;
        const seq = seqRef.current++;
        const buf = await ev.data.arrayBuffer();
        // Serialize uploads — keeps order, avoids burst on slow links
        uploadingRef.current = uploadingRef.current.then(() => uploadChunk(buf, seq));
      };
      rec.onerror = (ev: any) => {
        setState(s => ({ ...s, lastError: `recorder error: ${ev?.error?.message || "unknown"}` }));
      };
      rec.start(CHUNK_MS);
      setState(s => ({ ...s, recording: true, lastError: null }));
    }

    if (!active && recRef.current && !finalizedRef.current) {
      void stopAndFinalize();
    }

    async function uploadChunk(buf: ArrayBuffer, seq: number, attempt = 0): Promise<void> {
      const tok = sessionStorage.getItem("cognihome_access_token");
      try {
        const resp = await fetch(
          `${API_BASE}/api/sessions/${sessionIdRef.current}/recording/chunk?seq=${seq}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/octet-stream",
              "Authorization": `Bearer ${tok ?? ""}`,
            },
            body: buf,
          },
        );
        if (!resp.ok) throw new Error(`http ${resp.status}`);
        setState(s => ({ ...s, uploadedChunks: s.uploadedChunks + 1 }));
      } catch (e) {
        if (attempt < 1) return uploadChunk(buf, seq, attempt + 1);
        setState(s => ({
          ...s,
          failedChunks: s.failedChunks + 1,
          lastError: `chunk ${seq}: ${(e as Error).message}`,
        }));
      }
    }
  // sessionId is captured via ref, not a dep — keeps the recorder
  // running across props changes that don't matter for recording.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, stream]);

  // Unmount path — same finalize as the active→false branch. Without
  // this the trailing chunk + manifest write is lost when the case
  // closes the tab mid-session.
  useEffect(() => () => { void stopAndFinalize(); }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  return state;
}
